import { apiErrorField, apiErrorMessage } from '@agos/api-client'
import { useGetPaymentsByDriver } from '@agos/api-client/payments'
import {
  OrderNumberLink,
  T,
  formatCents,
  formatCount,
  formatDateTime,
  formatTime,
  paymentMethodBadge,
  tt,
} from '@agos/shared'
import {
  ButtonGroup,
  DateRangePicker,
  EmptyState,
  FilterSelect,
  ReadFailedBlock,
  Spinner,
  StatCard,
  StatusBadge,
  cn,
} from '@agos/ui'
import {
  AlertTriangle,
  Banknote,
  ChevronDown,
  ChevronRight,
  CreditCard,
  Info,
  QrCode,
  Truck,
  type LucideIcon,
} from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { useSearchParams } from 'wouter'

import {
  DRIVER_PAYMENT_CHECKS,
  DRIVER_PAYMENT_PRESETS,
  activePreset,
  checkLabel,
  checkOf,
  driverOptions,
  driverPaymentsOf,
  filterRows,
  gatewayLead,
  inTheFieldNote,
  methodChangeText,
  ordersByTime,
  presetLabel,
  presetWindow,
  railLabel,
  receiptText,
  reviewLabel,
  reviewTone,
  sortGroups,
  summaryCards,
  summaryLines,
  trailNote,
  unownedToShow,
  verdictLabel,
  verdictSentence,
  verdictTone,
  windowDays,
  windowFromSearch,
  type DriverPaymentsFilter,
  type DriverPaymentsOrder,
  type DriverPaymentsReport,
  type DriverPaymentsSummary,
  type DriverPaymentsWindow,
  type SummaryCard,
  type SummaryTone,
} from './driver-payments'
import { statusLabel } from './payment-archive'

/**
 * *Podľa rozvozárov* - each driver's orders, what the gateway says about each, and a summary under them. FRONTEND.md
 * §9.749, owner O121, on BACKEND.md §10A.403 (`GET /payments/by-driver`) and §10A.402 (the trail a close now writes).
 *
 * The owner: *"rozvozar Jožko bral obj 78 qr platba (úspešná) suma 20€ / 68 cardpay suma 10€ / A dole súhrn :
 * cardpay 10€ qr 20€"*. So: a card per driver, the orders by time, and the summary **below** them in that order.
 *
 * **The server decides and this screen draws.** Every verdict, flag and figure arrives computed over the handover's
 * own set of orders; the words, the order things are drawn in and the window are `driver-payments.ts`'s, which is
 * tested beside it. Nothing here sums money or re-derives a verdict - a second spelling of either on the client is how
 * two screens come to disagree about one order.
 *
 * **Mounted only while its tab is open**, so `GET /payments/by-driver` is asked only by somebody looking at it. The
 * window and both filters live in the URL (`?dateFrom=&dateTo=&driver=&check=`), so *Jožko, Na kontrolu, včera* is
 * something one person can send another and a reload keeps.
 */

/**
 * The two answers that are about the question rather than a fault - `DriverPayments.RANGE_INVALID` and
 * `RANGE_TOO_WIDE` (BACKEND.md §10A.403). **Keyed on the code, never on the sentence** - §9.620's rule: the server's
 * Slovak is what the screen shows, and a reworded message must not silently turn this branch into a failed read.
 */
const REFUSED_RANGES = ['DRIVER_PAYMENTS_RANGE_INVALID', 'DRIVER_PAYMENTS_RANGE_TOO_WIDE'] as const

/** The server's sentence for a refused window, or null when the failure is not a refusal of the window. */
function refusalOf(error: unknown): string | null {
  const code = apiErrorField(error, 'code')
  if (!REFUSED_RANGES.some((refused) => refused === code)) return null
  return apiErrorMessage(error, tt('Toto obdobie sa nedá zobraziť - vyberte iné.'))
}

export function DriverPaymentsPanel() {
  const [params, setParams] = useSearchParams()
  // Read on every render rather than once: *Dnes* has to mean today for a screen left open past midnight.
  const now = new Date()
  const period = windowFromSearch(params.get('dateFrom'), params.get('dateTo'), now)

  /* **Both bounds, always.** The server would read two absent bounds as *today* too, but in its zone and at its
     clock; sending the window the bar shows is what keeps the figures and the dates above them one statement. */
  const query = useGetPaymentsByDriver({ dateFrom: period.dateFrom, dateTo: period.dateTo })
  const report = useMemo(() => driverPaymentsOf(query.data), [query.data])

  const refusal = query.isError ? refusalOf(query.error) : null
  /* **A null report is a failed read too, not only `isError`.** The parser refuses an answer it cannot read whole - a
     missing figure, an unknown verdict - because drawing one as a nought is the fabricated all-clear FB-810 and FB-817
     are about. So an answer that arrived and did not parse is shown exactly as one that never arrived. */
  const failed = (query.isError && refusal === null) || (query.isSuccess && report === null)

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === '') next.delete(key)
      else next.set(key, value)
    }
    setParams(next, { replace: true })
  }

  const check = checkOf(params.get('check'))
  const requestedDriver = params.get('driver') ?? ''
  /* A driver the URL names who had no order in this window - a link from yesterday, or a window changed after
     choosing - reads as *everybody*, as `checkOf` reads a stale check: the select can only show a driver it lists,
     and a filter the select cannot show would hide every card behind *Všetci*. */
  const driverListed =
    report !== null && report.drivers.some((group) => String(group.employeeId) === requestedDriver)
  const filter: DriverPaymentsFilter = { driver: driverListed ? requestedDriver : '', check }

  const oneDay = windowDays(period) <= 1

  return (
    <div className="space-y-4" data-driver-payments>
      <div className="space-y-1.5 text-xs text-muted-foreground">
        {/* Chosen by `gatewayActive`, so it waits for the answer: Nesvady has no gateway, and a line claiming one is
            checked would be the wrong half of the screen's whole point. */}
        {report !== null && (
          <p
            className="flex items-start gap-2"
            data-driver-payments-gateway={report.gatewayActive ? 'on' : 'off'}
          >
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {gatewayLead(report.gatewayActive)}
          </p>
        )}
        {/* Always, once: §10A.402 writes a door change from this version on and back-fills nothing, so a row with no
            trail on an older order is *unknown*, never *unchanged*. */}
        <p className="flex items-start gap-2" data-driver-payments-trail-note>
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          {trailNote()}
        </p>
      </div>

      <WindowBar
        period={period}
        now={now}
        onChange={(next) => update({ dateFrom: next.dateFrom, dateTo: next.dateTo })}
      />

      {refusal !== null ? (
        /* A refused window is an answer about the question, not a fault: the server's own sentence says what to change,
           and the bar above stays so it can be changed. No retry - asking again asks the same thing. */
        <div
          role="alert"
          className="rounded-2xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm flex items-start gap-2"
          data-driver-payments-refused
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-500" />
          <span>{refusal}</span>
        </div>
      ) : failed ? (
        /* Before the cards and before the empty state: a failure must not draw *Chýba v bráne 0,00 €* in emerald or
           *no driver had an order*, which are the two claims this screen exists to make honestly. */
        <ReadFailedBlock
          marker="driver-payments"
          title={tt('Kontrolu rozvozárov sa nepodarilo načítať')}
          onRetry={() => void query.refetch()}
        >
          <T>Neznamená to, že je všetko v poriadku - len sme sa k údajom nedostali.</T>
        </ReadFailedBlock>
      ) : (
        <>
          <Cards totals={report === null ? null : report.totals} />

          {report === null ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : report.drivers.length === 0 && report.unownedDeliveries.summary.orderCount === 0 ? (
            <div data-driver-payments-empty>
              <EmptyState
                icon={<Truck className="w-10 h-10" />}
                title={tt('Žiadne objednávky')}
                description={tt('V tomto období nemal žiadny rozvozár objednávku.')}
              />
            </div>
          ) : (
            <DriverGroups
              report={report}
              filter={filter}
              oneDay={oneDay}
              windowKey={`${period.dateFrom}:${period.dateTo}`}
              onDriver={(driver) => update({ driver })}
              onCheck={(next) => update({ check: next === 'all' ? null : next })}
            />
          )}
        </>
      )}
    </div>
  )
}

/** *Dnes · Včera · 7 dní · Tento mesiac* and a date range - every preset within the server's 31 days. */
function WindowBar({
  period,
  now,
  onChange,
}: {
  period: DriverPaymentsWindow
  now: Date
  onChange: (next: DriverPaymentsWindow) => void
}) {
  const chosen = activePreset(period, now)
  return (
    <div className="flex flex-wrap items-center gap-2" data-driver-payments-window>
      <ButtonGroup
        options={DRIVER_PAYMENT_PRESETS.map((preset) => ({ value: preset, label: presetLabel(preset) }))}
        value={chosen ?? ''}
        onChange={(value) => {
          const preset = DRIVER_PAYMENT_PRESETS.find((candidate) => candidate === value)
          if (preset !== undefined) onChange(presetWindow(preset, now))
        }}
        testIdKey="data-driver-preset"
        // §9.749 - a thumb's 44 px on a phone; the manager's period look everywhere else.
        className="*:min-h-11 sm:*:min-h-0"
      />
      {/* Both bounds written whichever moved, so changing one keeps the other the screen was showing - the default
          window has no `?dateTo=` until somebody chooses it. */}
      <DateRangePicker
        from={period.dateFrom}
        to={period.dateTo}
        onFromChange={(value) => onChange({ dateFrom: value, dateTo: period.dateTo })}
        onToChange={(value) => onChange({ dateFrom: period.dateFrom, dateTo: value })}
        className="flex-wrap"
      />
    </div>
  )
}

function cardIcon(key: SummaryCard['key']): LucideIcon {
  switch (key) {
    case 'cardpay':
      return CreditCard
    case 'qrpay':
      return QrCode
    case 'missing':
      return AlertTriangle
  }
}

/** The report's totals, `-` until the read answers - never a fabricated nought (`summaryCards`). */
function Cards({ totals }: { totals: DriverPaymentsSummary | null }) {
  const cards = summaryCards(totals)
  return (
    <div
      className="grid grid-cols-2 gap-3 lg:grid-cols-3"
      data-driver-payments-cards
    >
      {cards.map((card) => (
        <StatCard
          key={card.key}
          label={card.label}
          value={card.value}
          sub={card.sub ?? undefined}
          color={card.color}
          icon={cardIcon(card.key)}
          testId={`driver-payments-${card.key}`}
        />
      ))}
    </div>
  )
}

function DriverGroups({
  report,
  filter,
  oneDay,
  windowKey,
  onDriver,
  onCheck,
}: {
  report: DriverPaymentsReport
  filter: DriverPaymentsFilter
  oneDay: boolean
  windowKey: string
  onDriver: (driver: string) => void
  onCheck: (check: string) => void
}) {
  const groups = sortGroups(filterRows(report.drivers, filter))
  const unowned = unownedToShow(report.unownedDeliveries, filter)
  const fieldNote = inTheFieldNote(report.totals.inTheFieldCount)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3" data-driver-payments-filters>
        <FilterSelect
          label={tt('Rozvozár')}
          showLabel
          value={filter.driver}
          onChange={onDriver}
          options={driverOptions(report.drivers)}
          testId="data-driver-filter"
        />
        <FilterSelect
          label={tt('Kontrola')}
          showLabel
          value={filter.check}
          onChange={onCheck}
          options={DRIVER_PAYMENT_CHECKS.map((entry) => ({ value: entry, label: checkLabel(entry) }))}
          testId="data-check-filter"
        />
      </div>
      {fieldNote !== '' && (
        <p className="text-xs text-amber-600 dark:text-amber-500" data-driver-payments-in-the-field>
          {fieldNote}
        </p>
      )}

      {groups.length === 0 && unowned === null ? (
        <p className="text-sm text-muted-foreground py-6 text-center" data-driver-payments-no-match>
          <T>Tomuto výberu nezodpovedá žiadna objednávka.</T>
        </p>
      ) : null}

      {groups.map((group) => (
        <GroupCard
          // The window in the key: a new window opens each card the way its own rule says, rather than the way it
          // was left for another day.
          key={`${group.employeeId}:${windowKey}`}
          attributes={{ 'data-driver-group': group.employeeId }}
          heading={
            <>
              <span className="font-bold text-foreground">{group.name}</span>
              {group.alsoDrives && (
                <span className="text-[11px] font-semibold text-muted-foreground" data-driver-also-drives>
                  <T>aj rozvoz</T>
                </span>
              )}
            </>
          }
          summary={group.summary}
          orders={group.orders}
          inHandover
          oneDay={oneDay}
        />
      ))}

      {/* Last, and only when it holds anything: deliveries nobody owns are money in no handover at all (#161). */}
      {unowned !== null && (
        <GroupCard
          key={`unowned:${windowKey}`}
          attributes={{ 'data-driver-unowned': true }}
          heading={
            <span className="font-bold text-foreground">
              <T>Rozvoz bez rozvozára</T>
            </span>
          }
          note={
            <p className="px-4 pb-2 text-xs text-amber-600 dark:text-amber-500" data-driver-unowned-note>
              <T>Tieto objednávky nie sú v žiadnom odovzdaní tržby - nikto za ne peniaze neodovzdáva.</T>
            </p>
          }
          summary={unowned.summary}
          orders={unowned.orders}
          inHandover={false}
          oneDay={oneDay}
        />
      )}
    </div>
  )
}

/**
 * One driver, or the deliveries nobody owns: a header that folds the rows, the rows by time, and the summary below.
 *
 * **Open by default for a one-day window or when something is flagged** - a day is what somebody checks after
 * closing, and a flagged card is what they came for. **The summary stays when the rows fold**, because it is the
 * owner's own line and a week's check reads card after card of it.
 */
function GroupCard({
  attributes,
  heading,
  note,
  summary,
  orders,
  inHandover,
  oneDay,
}: {
  attributes: Record<string, string | number | boolean>
  heading: ReactNode
  note?: ReactNode
  summary: DriverPaymentsSummary
  orders: readonly DriverPaymentsOrder[]
  inHandover: boolean
  oneDay: boolean
}) {
  const [open, setOpen] = useState(oneDay || summary.flaggedCount > 0)
  const review = reviewLabel(summary.flaggedCount)

  return (
    <section className="bg-card border border-border rounded-2xl overflow-hidden" {...attributes}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        data-driver-toggle
        className="w-full min-h-11 flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2.5 text-left hover:bg-muted/30 transition-colors"
      >
        {open ? (
          <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />
        )}
        {heading}
        <span className="text-xs text-muted-foreground tabular-nums">
          {formatCount(summary.orderCount)} {tt('obj.')}
        </span>
        {review !== '' && (
          <span
            className={cn(
              'text-xs font-bold',
              reviewTone(summary) === 'red' ? 'text-red-500' : 'text-amber-600 dark:text-amber-500',
            )}
            data-driver-review={reviewTone(summary)}
          >
            {review}
          </span>
        )}
      </button>
      {note}
      {open && (
        <div className="divide-y divide-border border-t border-border" data-driver-rows>
          {orders.length === 0 ? (
            <p className="px-4 py-3 text-xs text-muted-foreground" data-driver-rows-none>
              <T>Bez objednávok na kontrolu - ostatné sú započítané len v súhrne.</T>
            </p>
          ) : (
            ordersByTime(orders).map((order) => (
              <OrderRow key={order.orderId} order={order} inHandover={inHandover} oneDay={oneDay} />
            ))
          )}
        </div>
      )}
      <SummaryBlock summary={summary} inHandover={inHandover} />
    </section>
  )
}

/** The badge's own icon - the same three the board and the till draw (§9.204, §9.741). */
function MethodGlyph({ icon }: { icon: string | undefined }) {
  if (icon === 'cash') return <Banknote className="w-2.5 h-2.5" />
  if (icon === 'qr') return <QrCode className="w-2.5 h-2.5" />
  return <CreditCard className="w-2.5 h-2.5" />
}

/**
 * One order, as one element - not `DataTable`, which renders every row twice (a table and a phone card), so a row
 * selected by its marker would be two. Stacked by wrapping on a phone, one line on a desktop.
 */
function OrderRow({
  order,
  inHandover,
  oneDay,
}: {
  order: DriverPaymentsOrder
  inHandover: boolean
  oneDay: boolean
}) {
  const [open, setOpen] = useState(false)
  const sentence = verdictSentence(order, inHandover)
  const method = paymentMethodBadge(order.paymentMethod)
  const number = order.orderNumber ?? `#${order.orderId}`

  return (
    <div
      className="px-4 py-2 space-y-1"
      data-driver-row={order.orderId}
      data-verdict={order.verdict}
      data-flagged={order.flagged ? 'true' : 'false'}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <OrderNumberLink
          orderId={order.orderId}
          className="font-mono text-xs font-semibold text-primary hover:underline md:w-24"
          title={`${tt('Otvoriť v archíve')}: ${number}`}
        >
          {number}
        </OrderNumberLink>
        <span className="text-xs text-muted-foreground tabular-nums md:w-32">
          {oneDay ? formatTime(order.effectiveAt) : formatDateTime(order.effectiveAt)}
        </span>
        {method === null ? (
          <span className="text-xs text-muted-foreground">-</span>
        ) : (
          <span
            data-payment-method={order.paymentMethod}
            className={cn(
              'inline-flex items-center gap-0.5 text-[10px] font-bold rounded px-1 py-0.5 border leading-none',
              method.tone,
            )}
          >
            <MethodGlyph icon={method.icon} />
            {method.label}
          </span>
        )}
        <span className="text-sm font-semibold tabular-nums" data-driver-row-total>
          {formatCents(order.totalCents)}
        </span>
        <StatusBadge
          label={verdictLabel(order)}
          color={verdictTone(order.verdict)}
          className="whitespace-normal"
        />
        <button
          type="button"
          aria-expanded={open}
          aria-label={`${tt('Podrobnosti')} ${number}`}
          onClick={() => setOpen((was) => !was)}
          data-driver-row-toggle
          className="ml-auto min-h-11 min-w-11 inline-flex items-center justify-center rounded-lg text-muted-foreground hover:bg-muted/40 transition-colors"
        >
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </div>
      {sentence !== '' && (
        <p className="text-xs text-muted-foreground break-words" data-verdict-sentence>
          {sentence}
        </p>
      )}
      {open && <RowDetails order={order} />}
    </div>
  )
}

/** Every gateway attempt, the method's trail, and the receipt - what a row's verdict was decided from. */
function RowDetails({ order }: { order: DriverPaymentsOrder }) {
  return (
    <div className="mt-1 space-y-2 rounded-xl bg-muted/30 p-3 text-xs" data-driver-row-details>
      <div className="space-y-1">
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <T>Platby v bráne</T>
        </p>
        {order.sessions.length === 0 ? (
          <p className="text-muted-foreground">
            <T>Žiadny pokus o platbu v bráne.</T>
          </p>
        ) : (
          order.sessions.map((session) => (
            <p
              key={session.paymentId}
              className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5"
              data-driver-session={session.paymentId}
              data-session-status={session.status}
            >
              {/* *kartou* / *prevodom* rather than *CardPay* / *QR Pay*: on this screen those two name the order's
                  method, and a door QR paid by card reads `card_pay` here while the order is `qr_pay`. */}
              <span className="font-semibold">{railLabel(session.rail)}</span>
              <span>{statusLabel(session.status)}</span>
              <span className="tabular-nums">{formatCents(session.amountCents)}</span>
              <span className="text-muted-foreground">{formatDateTime(session.createdAt)}</span>
              {session.transactionId !== null && (
                <span className="font-mono text-[10px] text-muted-foreground select-all break-all">
                  {session.transactionId}
                </span>
              )}
            </p>
          ))
        )}
      </div>
      <div className="space-y-1">
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <T>Zmeny spôsobu platby</T>
        </p>
        {order.methodChanges.length === 0 ? (
          <p className="text-muted-foreground">
            <T>Žiadna zmena nie je zaznamenaná.</T>
          </p>
        ) : (
          order.methodChanges.map((change, index) => (
            <p key={`${change.at}:${index}`} className="break-words" data-method-change>
              {methodChangeText(change)}
            </p>
          ))
        )}
      </div>
      <p data-driver-row-receipt>{receiptText(order)}</p>
    </div>
  )
}

function summaryToneClass(tone: SummaryTone): string {
  switch (tone) {
    case 'plain':
      return 'text-foreground'
    case 'amber':
      return 'text-amber-600 dark:text-amber-500'
    case 'red':
      return 'text-red-500 font-semibold'
    case 'total':
      return 'text-foreground font-bold border-t border-border pt-1 mt-1'
  }
}

/**
 * The owner's *"A dole súhrn"* - `summaryLines`, below the rows, in the owner's order. `inHandover` is false for the
 * deliveries nobody owns, whose summary must not speak of a handover they are in none of.
 */
function SummaryBlock({
  summary,
  inHandover,
}: {
  summary: DriverPaymentsSummary
  inHandover: boolean
}) {
  return (
    <div className="border-t border-border bg-muted/10 px-4 py-3 space-y-0.5 text-sm" data-driver-summary>
      <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        <T>Súhrn</T>
      </p>
      {summaryLines(summary, inHandover).map((line) => (
        <p key={line.key} className={cn('tabular-nums break-words', summaryToneClass(line.tone))} data-summary-line={line.key}>
          {line.text}
        </p>
      ))}
    </div>
  )
}
